import React from "react";
import styles from "./FHIRPatient.module.scss";
import DateTimePicker from "react-datetime-picker";
import { capitalize } from "../../utils/textFunctions";
import RecordOverrideOptions from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideOptions";
import AuxDataUpdateOptions from "../../routes/NewSearchRecords/SearchRecordTable/AggregateAuxUpdate/AuxDataUpdateOptions";
import { overrideSummaryForIdentifier } from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideUtil";
import { handleMultipleAttributeOverride } from "../../routes/NewSearchRecords/SearchRecordTable/MultipleAttributeOverride/Utils";
import { formattedDateWithDefault } from "../../utils/formattedDate";
import Modal from "../../components/ModalPopup";
import ButtonList from "../../components/ButtonList";
import Button from "../../components/Button";
import { isImdmClient } from "../../utils/isImdmClient";
import { useAuthState } from "../../components/AuthProvider";
import LinkButton from "../../components/LinkButton";
import OverrideAuditInputModal from "../../routes/NewSearchRecords/SearchRecordTable/OverrideAuditInputModal";
import {
  aggregateAuxDataEditableList,
  identifierUpdateDialogContext,
  aggregateAuxDataSystemOptions,
  updateAggregateAuxData,
  deleteAggregateAuxData,
  newAggregateAuxData
} from "../../routes/NewSearchRecords/SearchRecordTable/AggregateAuxUpdate/AggregateAuxUpdateUtil";

const Identifier = ({
  data,
  summary: isSummary,
  patientId,
  oncRecordOverrideIconClick,
  onAggregateAuxDataUpdate,
  className,
  isAggregateInfo = false,
  isExcludedValues = false,
  isAdditionalInformation = false,
  isInvisible = false,
  sourceCode,
  isNewQuarantine = false,
  isMultipleAttributeOverride = false,
  setAttributeOverrides,
  attributeOverrides
}) => {
  const { user } = useAuthState();
  const { selectedClient } = user.preferences;
  const isImdm = isImdmClient(selectedClient);
  const [value, setValue] = React.useState("");
  const [valueChanged, setValueChanged] = React.useState(false);
  const [system, setSystem] = React.useState("");
  const [sourceTimeStamp, setSourceTimeStamp] = React.useState(new Date());
  const [
    identifierActionDialogContent,
    setIdentifierActionDialogContent
  ] = React.useState({});
  const [identifierActionDialog, setIdentifierActionDialog] = React.useState(
    false
  );
  const [showAuditModal, setShowAuditModal] = React.useState(false);
  const [pendingConfirmDetails, setPendingConfirmDetails] = React.useState(
    null
  );

  if (isSummary) {
    return null;
  }

  const prepareOverrideOriginalValue = (identifier, type) => {
    const extensionMap = overrideSummaryForIdentifier(identifier, type);
    oncRecordOverrideIconClick(extensionMap);
  };

  const actionHandler = detailsMap => {
    if (isMultipleAttributeOverride) {
      handleMultipleAttributeOverride(
        detailsMap,
        "identifier",
        attributeOverrides,
        setAttributeOverrides
      );
      setIdentifierActionDialog(false);
    } else {
      oncRecordOverrideIconClick(detailsMap);
    }
  };

  const renderActionColumn = () => {
    return (
      !isAggregateInfo ||
      (isAggregateInfo && !isExcludedValues && !isAdditionalInformation)
    );
  };

  const renderAddNewAggregateAuxIdentifierLink = () => {
    return (
      isAggregateInfo &&
      !isExcludedValues &&
      !isAdditionalInformation &&
      !isInvisible
    );
  };

  const handleIdentifierAction = detailsMap => {
    switch (detailsMap["Mark as"]) {
      // For inactive and invalid, send it to the recordTable where it will be handled there.
      case "Inactive":
        actionHandler(detailsMap);
        break;
      case "Invalid":
        actionHandler(detailsMap);
        break;
      case "No Change":
        handleMultipleAttributeOverride(
          detailsMap,
          "identifier",
          attributeOverrides,
          setAttributeOverrides
        );
        break;
      default:
        // For Replace and aggregateAuxData actions, open the identifier action modal.
        if (detailsMap.originalFieldData) {
          setValue(detailsMap.originalFieldData.value);
        }
        setSourceTimeStamp(
          detailsMap["Source Timestamp"] &&
            detailsMap["Source Timestamp"] !== "-"
            ? new Date(detailsMap["Source Timestamp"])
            : new Date()
        );
        setIdentifierActionDialogContent(detailsMap);
        setIdentifierActionDialog(true);
        break;
    }
  };

  const handleConfirmIdentifierAction = () => {
    const finalMap = { ...identifierActionDialogContent };

    if (!finalMap.originalFieldData.period)
      finalMap.originalFieldData.period = {};
    finalMap.originalFieldData.period.start = sourceTimeStamp.toISOString();

    switch (finalMap["Mark as"]) {
      case "Replace":
        finalMap.originalFieldData.value = value;
        if (isMultipleAttributeOverride)
          // Directly proceed to next step without audit modal
          actionHandler(finalMap);
        else {
          // Set pending confirm details to show audit modal later
          setPendingConfirmDetails(finalMap);
          setShowAuditModal(true);
        }
        break;
      case newAggregateAuxData:
        finalMap.originalFieldData.system = system;
        finalMap.originalFieldData.value = value;
        onAggregateAuxDataUpdate(finalMap);
        break;
      case updateAggregateAuxData:
        finalMap.originalFieldData.value = value;
        onAggregateAuxDataUpdate(finalMap);
        break;
      case deleteAggregateAuxData:
        finalMap.originalFieldData.value = "";
        onAggregateAuxDataUpdate(finalMap);
        break;
      default:
        break;
    }
  };

  const handleAuditSubmit = auditInfo => {
    if (pendingConfirmDetails) {
      const updatedDetails = {
        ...pendingConfirmDetails,
        auditInfoList: auditInfo
      };
      actionHandler(updatedDetails);
      setPendingConfirmDetails(null);
    }
    setShowAuditModal(false);
  };

  const identifierActionModal = () => {
    return (
      <Modal
        open={identifierActionDialogContent}
        onClose={() => setIdentifierActionDialog(false)}
      >
        <h3 style={{ textAlign: "center" }}>
          {identifierUpdateDialogContext(
            identifierActionDialogContent,
            "title"
          )}
        </h3>
        <br />
        <br />
        <h4 style={{ textAlign: "left" }}>
          {identifierUpdateDialogContext(
            identifierActionDialogContent,
            "description"
          )}
        </h4>
        <br />
        <div>
          {identifierActionDialogContent && (
            <table
              className={
                identifierActionDialogContent["Mark as"] === "Replace"
                  ? "recOvrTable"
                  : "updateTable"
              }
            >
              <tr>
                <th class="system">System</th>
                <th class="value">Value</th>
                <th class="use">Use</th>
                <th class="timestamp">Source Timestamp</th>
              </tr>
              <tr>
                <td>
                  {identifierActionDialogContent["Mark as"] ===
                    newAggregateAuxData && (
                    <select
                      name="aggregateAuxSystem"
                      value={system}
                      onChange={event => setSystem(event.target.value)}
                    >
                      <option id="0" value="">
                        Select System
                      </option>
                      {aggregateAuxDataSystemOptions(data).map(system => (
                        <option id="1" value={system}>
                          {system}
                        </option>
                      ))}
                    </select>
                  )}
                  {identifierActionDialogContent["Mark as"] !==
                    newAggregateAuxData && (
                    <input
                      type="text"
                      value={identifierActionDialogContent["System"]}
                      disabled={true}
                    />
                  )}
                </td>
                <td>
                  <input
                    type="text"
                    value={value}
                    disabled={
                      identifierActionDialogContent["Mark as"] ===
                      deleteAggregateAuxData
                    }
                    onChange={e => {
                      setValue(e.target.value);
                      e.target.value === identifierActionDialogContent["Value"]
                        ? setValueChanged(false)
                        : setValueChanged(true);
                    }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={identifierActionDialogContent["Use"]}
                    disabled={true}
                  />
                </td>
                <td>
                  <DateTimePicker
                    format="MM-dd-y hh:mm:ss a"
                    value={sourceTimeStamp}
                    onChange={changeDate => {
                      setSourceTimeStamp(changeDate);
                      if (!valueChanged) {
                        setValueChanged(true);
                      }
                    }}
                    disabled={
                      identifierActionDialogContent["Mark as"] ===
                      deleteAggregateAuxData
                    }
                  />
                </td>
              </tr>
            </table>
          )}
        </div>
        <br />
        <br />
        <div style={{ marginTop: "1rem" }}>
          <ButtonList
            style={{
              display: "flex",
              justifyContent: "center"
            }}
          >
            <Button
              primary
              onClick={() => handleConfirmIdentifierAction()}
              disabled={
                identifierActionDialogContent["Mark as"] === newAggregateAuxData
                  ? !valueChanged ||
                    value === "" ||
                    system === "" ||
                    (typeof value !== "undefined" && value.indexOf(" ") >= 0)
                  : identifierActionDialogContent["Mark as"] ===
                      updateAggregateAuxData ||
                    identifierActionDialogContent["Mark as"] === "Replace"
                  ? !valueChanged ||
                    value === "" ||
                    (typeof value !== "undefined" && value.indexOf(" ") >= 0)
                  : false
              }
            >
              Confirm
            </Button>
            <Button
              onClick={() => {
                setValueChanged(false);
                setIdentifierActionDialog(false);
              }}
            >
              Cancel
            </Button>
          </ButtonList>
        </div>
      </Modal>
    );
  };

  const aggregateAuxUpdateOptions = identifierMap => {
    if (
      isAggregateInfo &&
      !isInvisible &&
      aggregateAuxDataEditableList.includes(identifierMap["System"])
    ) {
      return (
        <AuxDataUpdateOptions
          detailsMap={JSON.parse(JSON.stringify(identifierMap))}
          onClickAuxDataUpdateAction={detailsMap =>
            handleIdentifierAction(detailsMap)
          }
        />
      );
    }
    return null;
  };

  const addNewAggregateAuxIdentifierLink = () => {
    return (
      <LinkButton
        stickLeft={true}
        icon={"add"}
        onClick={() =>
          handleIdentifierAction({
            Field: "Identifier",
            originalFieldData: {},
            "Mark as": newAggregateAuxData,
            originalFieldName: "identifier"
          })
        }
        iconClassName={styles.addNewOverrodeIcon}
      >
        {"Add Auxiliary Identifier"}
      </LinkButton>
    );
  };

  if (!isSummary && data.length === 0) {
    return (
      <section>
        {
          <>
            <h3>Identifier</h3>
            <div>
              <h4>Empty</h4>
            </div>
          </>
        }
        {identifierActionDialog && identifierActionModal()}
        {renderAddNewAggregateAuxIdentifierLink() &&
          addNewAggregateAuxIdentifierLink()}
      </section>
    );
  }

  return (
    <>
      {showAuditModal && (
        <OverrideAuditInputModal
          isOpen={showAuditModal}
          onCancel={() => {
            setShowAuditModal(false);
            setPendingConfirmDetails(null);
          }}
          onSubmit={handleAuditSubmit}
        />
      )}
      <section>
        <div className={styles.identifierDiv + " " + styles.header}>
          {renderActionColumn() && (
            <div
              className={
                isMultipleAttributeOverride
                  ? styles.multipleAttributeOverrideColumn
                  : styles.actionColumn
              }
            />
          )}
          <div>
            <h4>System</h4>
          </div>
          <div>
            <h4>Code</h4>
          </div>
          <div>
            <h4>Value</h4>
          </div>
          <div>
            <h4>Use</h4>
          </div>
          <div>
            <h4>Source</h4>
          </div>
          <div>
            <h4>Source Timestamp</h4>
          </div>
        </div>

        {data.map((id, index) => {
          const identifierMap = {
            Field: "Identifiers",
            System: id.system ? id.system : "-",
            Code:
              id.type &&
              id.type.coding &&
              id.type.coding.filter(
                e =>
                  e.hasOwnProperty("system") &&
                  e.system === "EIMP-MRN" &&
                  e.hasOwnProperty("code")
              ).length > 0
                ? id.type.coding.filter(
                    e =>
                      e.hasOwnProperty("system") &&
                      e.system === "EIMP-MRN" &&
                      e.hasOwnProperty("code")
                  )[0].code
                : id.type &&
                  id.type.coding &&
                  id.type.coding.filter(e => e.hasOwnProperty("code")).length >
                    0
                ? id.type.coding.filter(e => e.hasOwnProperty("code"))[0].code
                : "-",
            Value: id.value ? id.value : "-",
            Use: id.use ? capitalize(id.use) : "-",
            "Source Timestamp":
              id.period && id.period.start
                ? formattedDateWithDefault(id.period.start)
                : "-",
            "Mark as": "",
            Source:
              id.extension &&
              id.extension.filter(e => e.url.includes("#sourceCode")).length > 0
                ? id.extension.filter(e => e.url.includes("#sourceCode"))[0]
                    .valueString
                : "-",
            originalFieldName: "identifier",
            originalFieldData: id,
            originalFieldDataCopy: JSON.parse(JSON.stringify(id)),
            id: index
          };

          if (
            !isAggregateInfo ||
            !isImdm ||
            !id.system ||
            (id.system &&
              !user.preferences.sources.hasOwnProperty(id.system) &&
              id.system !== "imdm")
          ) {
            return (
              <div key={index} className={styles.identifierDiv}>
                {isAggregateInfo &&
                  !isExcludedValues &&
                  !isAdditionalInformation && (
                    <div
                      className={styles.actionColumn}
                      style={{ zIndex: 998 - index }}
                    >
                      {aggregateAuxUpdateOptions(identifierMap)}
                    </div>
                  )}
                {!isAggregateInfo && (
                  <div
                    className={
                      isMultipleAttributeOverride
                        ? styles.multipleAttributeOverrideColumn
                        : styles.actionColumn
                    }
                    style={{ zIndex: 998 - index }}
                  >
                    {oncRecordOverrideIconClick &&
                      !isInvisible &&
                      !isNewQuarantine &&
                      !(
                        (patientId === identifierMap["Value"] &&
                          sourceCode === identifierMap["System"]) ||
                        "imdm" === identifierMap["System"]
                      ) && (
                        <RecordOverrideOptions
                          detailsMap={identifierMap}
                          onClickRecordOverrideAction={detailsMap =>
                            handleIdentifierAction(detailsMap)
                          }
                          onClickRecordOverrideButton={type =>
                            prepareOverrideOriginalValue(
                              identifierMap.originalFieldData,
                              type
                            )
                          }
                          extension={
                            id.hasOwnProperty("extension")
                              ? id.extension.filter(
                                  item =>
                                    item.url ===
                                    "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override"
                                )
                              : []
                          }
                          isMultipleAttributeOverride={
                            isMultipleAttributeOverride
                          }
                          selectedOverrides={attributeOverrides.identifier}
                        />
                      )}
                  </div>
                )}
                <div>
                  <div>{identifierMap["System"]}</div>
                </div>
                <div>
                  <div>{identifierMap["Code"]}</div>
                </div>
                <div>
                  <div>{identifierMap["Value"]}</div>
                </div>
                <div>
                  <div>{identifierMap["Use"]}</div>
                </div>
                <div>
                  <div>{identifierMap["Source"]}</div>
                </div>
                <div>
                  <div>{identifierMap["Source Timestamp"]}</div>
                </div>
              </div>
            );
          }
        })}
        {identifierActionDialog && identifierActionModal()}
        {renderAddNewAggregateAuxIdentifierLink() &&
          addNewAggregateAuxIdentifierLink()}
      </section>
    </>
  );
};

export default Identifier;
